<template>
  <!-- HelpBar -->
  <HelpBar />

  <v-card class="actions-card" elevation="2">
    <!-- Header / Toolbar -->
    <v-toolbar flat class="actions-toolbar">
      <div class="actions-toolbar-content">
        <div class="actions-title-block">
          <div class="actions-title-icon">
            <v-icon color="primary" size="28">mdi-view-list</v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Maßnahmen
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Maßnahmen gruppieren, bearbeiten und für die Analyse auswählen.
            </div>
          </div>
        </div>

        <div class="actions-toolbar-actions">
          <!-- Ersetzt den früheren „Analyse"-Knopf: die nächste Seite der
               Projektstrecke ist ohnehin „Einsparpotential" (ProjectCalc),
               siehe config/projectPages.ts. -->
          <ProjectPageNav />

          <!-- Hilfe toggle -->
          <v-tooltip :disabled="store.sidebarShown" text="Hilfe anzeigen" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                variant="flat"
                class="toolbar-button"
                data-help-trigger
                @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ProjectDashboardHelp)"
              >
                <v-icon start>
                  {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                </v-icon>
                Hilfe
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-toolbar>

    <v-divider />

    <!-- Main Content -->
    <v-card-text class="actions-card-body">
      <!-- Summary / Controls Bar -->
      <v-sheet class="dashboard-summary" elevation="0">
        <div class="dashboard-summary-left">
          <v-chip color="primary" variant="tonal" class="font-weight-medium">
            <v-icon start>mdi-format-list-bulleted</v-icon>
            {{ actionList.length }} Maßnahme{{ actionList.length === 1 ? '' : 'n' }}
          </v-chip>

          <v-chip color="secondary" variant="tonal" class="font-weight-medium">
            <v-icon start>mdi-folder-outline</v-icon>
            {{ groupsList.length }} Gruppe{{ groupsList.length === 1 ? '' : 'n' }}
          </v-chip>
        </div>

        <div class="dashboard-summary-right">
          <span class="text-body-2 text-medium-emphasis">
            Auswahl zur Analyse über Checkboxen steuern
          </span>
        </div>
      </v-sheet>

      <!-- Empty State -->
      <div v-if="!actionList.length" class="empty-state">
        <v-sheet class="empty-state-sheet pa-8 text-center">
          <div class="empty-icon">
            <v-icon size="58" color="primary">mdi-playlist-plus</v-icon>
          </div>

          <div class="text-h6 font-weight-bold mb-1">
            Noch keine Maßnahmen vorhanden
          </div>

          <div class="text-medium-emphasis mb-5">
            Fügen Sie eine neue Maßnahme hinzu oder erstellen Sie zuerst eine Gruppe.
          </div>

          <div class="empty-actions">
            <v-dialog v-model="dialogCreateGroup" persistent max-width="640">
              <template #activator="{ props }">
                <v-btn color="primary" variant="tonal" v-bind="props">
                  <v-icon start>mdi-folder-plus</v-icon>
                  Gruppe hinzufügen
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="text-h6">Gruppe hinzufügen</v-card-title>
                <v-card-text>
                  <v-text-field label="Name" v-model="newActionGroupName" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="red" variant="text" @click="cancelCreateGroup">
                    Abbrechen
                  </v-btn>
                  <v-btn
                    color="primary"
                    variant="flat"
                    :disabled="!newActionGroupName"
                    @click="newActionGroup"
                  >
                    Speichern
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-btn color="primary" @click="newAction">
              <v-icon start>mdi-plus</v-icon>
              Maßnahme hinzufügen
            </v-btn>
          </div>
        </v-sheet>
      </div>

      <!-- Table -->
      <div v-else class="table-wrapper">
        <v-table class="actions-table" density="comfortable">
          <thead>
            <tr>
              <th scope="col" class="text-left col-name sticky-header">Name</th>
              <th scope="col" class="text-left col-method sticky-header">Methode</th>
              <th scope="col" class="text-left col-method-type sticky-header">Methodentyp</th>
              <th scope="col" class="text-right col-options sticky-header">Optionen</th>
            </tr>
          </thead>

          <!-- Group blocks -->
          <template v-for="(actions, groupKey) in byActionGroup" :key="String(groupKey)">
            <tbody>
              <!-- Group header row -->
              <tr class="group-row" :class="{ 'group-ungrouped': isUngroupedKey(groupKey) }">
                <td colspan="4" class="group-cell">
                  <div class="group-row-content">
                    <div class="group-title">
                      <v-icon
                        class="mr-3 group-title-icon"
                        size="22"
                        :color="isUngroupedKey(groupKey) ? 'secondary' : 'primary'"
                      >
                        {{ isUngroupedKey(groupKey) ? 'mdi-folder-off-outline' : 'mdi-folder-outline' }}
                      </v-icon>

                      
                      <div class="group-title-text">
                        <div class="group-title-main">
                          {{ isUngroupedKey(groupKey) ? 'Ohne Zuordnung' : getGroupName(groupKey) }}
                        </div>

                        <div class="group-title-meta">
                          {{ isUngroupedKey(groupKey) ? 'Nicht gruppiert' : 'Maßnahmengruppe' }}
                          ·
                          {{ actions.length }} Maßnahme{{ actions.length === 1 ? '' : 'n' }}
                        </div>
                      </div>
                    </div>

                    <!-- Right-side controls -->
                    <div v-if="!isUngroupedKey(groupKey)" class="group-controls">
                      <!-- Copy Group -->
                      <v-tooltip text="Gruppe kopieren" location="bottom" color="surface" content-class="tooltip">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            variant="text"
                            color="primary"
                            class="action-icon-button"
                            @click.stop="cloneActionGroup(getGroup(groupKey))"
                            aria-label="Gruppe kopieren"
                          >
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>

                      <!-- Rename Group -->
                      <v-dialog v-model="dialogRenameGroup" persistent max-width="640">
                        <template #activator="{ props }">
                          <v-tooltip text="Gruppe umbenennen" location="bottom" color="surface" content-class="tooltip">
                            <template #activator="{ props: tooltipProps }">
                              <v-btn
                                v-bind="{ ...props, ...tooltipProps }"
                                icon
                                variant="text"
                                color="primary"
                                class="action-icon-button"
                                @click.stop="
                                  storeGroupIdForDialog(groupKey);
                                  newActionGroupName = getGroupName(groupKey);
                                "
                                aria-label="Gruppe umbenennen"
                              >
                                <v-icon>mdi-square-edit-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>

                        <v-card>
                          <v-card-title class="text-h6">
                            Gruppe umbenennen
                          </v-card-title>

                          <v-card-text>
                            <v-text-field label="Name" v-model="newActionGroupName" />
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer />
                            <v-btn color="red" variant="text" @click="cancelRenameGroup">
                              Abbrechen
                            </v-btn>
                            <v-btn
                              color="primary"
                              variant="flat"
                              :disabled="!newActionGroupName"
                              @click="renameActionGroup"
                            >
                              Speichern
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>

                      <!-- Delete Group -->
                      <v-dialog v-model="dialogDeleteGroup" persistent max-width="420">
                        <template #activator="{ props }">
                          <v-tooltip text="Gruppe löschen" location="bottom" color="surface" content-class="tooltip">
                            <template #activator="{ props: tooltipProps }">
                              <v-btn
                                v-bind="{ ...props, ...tooltipProps }"
                                icon
                                variant="text"
                                color="red"
                                class="action-icon-button action-delete-button"
                                @click.stop="storeGroupIdForDialog(groupKey)"
                                aria-label="Gruppe löschen"
                              >
                                <v-icon>mdi-delete-outline</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>

                        <v-card>
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="red">mdi-alert</v-icon>
                            <span class="text-h6">Gruppe löschen?</span>
                          </v-card-title>

                          <v-card-text>
                            Alle Maßnahmen werden aus Gruppe und Projekt gelöscht.
                            Diese Aktion kann nicht rückgängig gemacht werden.
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer />
                            <v-btn variant="text" @click="dialogDeleteGroup = false">
                              Abbrechen
                            </v-btn>
                            <v-btn
                              color="red"
                              variant="flat"
                              @click="deleteActionGroup(getGroup(groupKey))"
                            >
                              Löschen
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>

                      <!-- Group Berechnen Checkbox -->
                      <v-tooltip text="Maßnahmengruppe berechnen" location="bottom" color="surface" content-class="tooltip">
                        <template #activator="{ props }">
                          <v-checkbox
                            v-bind="props"
                            class="group-calc-checkbox"
                            density="compact"
                            hide-details
                            :model-value="store.isGroupChecked(Number(groupKey))"
                            @click.stop
                            @update:modelValue="(newVal) => store.setGroupChecked(Number(groupKey), newVal)"
                            :indeterminate="store.isGroupIndeterminate(Number(groupKey))"
                          />
                        </template>
                      </v-tooltip>
                    </div>                 
                  </div>
                </td>
              </tr>

              <!-- Actions -->
              <tr
                v-for="action in actions"
                :key="action.id"
                class="pointer action-row"
                tabindex="0"
                @click="editAction(action)"
                @keydown.enter="editAction(action)"
              >

                <td class="text-left col-name">
                  <div class="action-name-cell">
                    <v-avatar size="34" color="primary" variant="tonal" class="mr-3">
                      <v-icon size="19" color="primary">{{ methodIcon(action) }}</v-icon>
                    </v-avatar>

                    <div class="action-name-text">
                      <div class="text-body-1 font-medium text-truncate">
                        {{ action.name }}
                      </div>

                      <div v-if="action.partial_plan" class="text-caption text-error">
                        Entwurf
                      </div>
                    </div>
                  </div>
                </td>

                <td class="text-left col-method">
                  <span class="text-truncate">
                    {{ checkMethodTexts(action) || '-' }}
                  </span>
                </td>

                <td class="text-left col-method-type">
                  <v-chip
                    size="small"
                    color="secondary"
                    variant="tonal"
                    class="method-type-chip"
                  >
                    {{ action.method_type || '-' }}
                  </v-chip>
                </td>

                <td class="text-right col-options">
                  <div class="action-controls">
                    <!-- Copy Action -->
                    <v-tooltip text="Kopie erstellen" location="bottom" color="surface" content-class="tooltip">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon
                          variant="text"
                          color="primary"
                          class="action-icon-button"
                          @click.stop="cloneAction(action)"
                          aria-label="Maßnahme kopieren"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>

                    <!-- Rename Action -->
                    <v-dialog v-model="dialogRenameAction" persistent max-width="640">
                      <template #activator="{ props }">
                        <v-tooltip text="Umbenennen" location="bottom" color="surface" content-class="tooltip">
                          <template #activator="{ props: tooltipProps }">
                            <v-btn
                              v-bind="{ ...props, ...tooltipProps }"
                              icon
                              variant="text"
                              color="primary"
                              class="action-icon-button"
                              @click.stop="
                                storeActionDataForDialog(action);
                                newActionName = action.name;
                              "
                              aria-label="Maßnahme umbenennen"
                            >
                              <v-icon>mdi-square-edit-outline</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>

                      <v-card>
                        <v-card-title class="text-h6">
                          Maßnahme umbenennen
                        </v-card-title>

                        <v-card-text>
                          <v-text-field label="Name" v-model="newActionName" />
                        </v-card-text>

                        <v-card-actions>
                          <v-spacer />
                          <v-btn color="red" variant="text" @click="cancelRenameAction">
                            Abbrechen
                          </v-btn>
                          <v-btn
                            color="primary"
                            variant="flat"
                            :disabled="!newActionName"
                            @click="renameAction(newActionName, dialogActionData)"
                          >
                            Speichern
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>

                    <!-- Assign Group -->
                    <v-menu>
                      <template #activator="{ props }">
                        <v-tooltip text="Gruppe zuordnen" location="bottom" color="surface" content-class="tooltip">
                          <template #activator="{ props: tooltipProps }">
                            <v-btn
                              v-bind="{ ...props, ...tooltipProps }"
                              icon
                              variant="text"
                              color="primary"
                              class="action-icon-button"
                              @click.stop
                              aria-label="Gruppe zuordnen"
                            >
                              <v-icon>mdi-folder-move-outline</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>

                      <v-list density="comfortable" class="assign-menu">
                        <v-list-subheader>Gruppen</v-list-subheader>

                        <v-list-item
                          v-for="(item, index) in groupsList"
                          :key="'g-' + index"
                        >
                          <v-btn
                            variant="text"
                            block
                            class="justify-start"
                            @click.stop="moveToGroup(item, action)"
                          >
                            {{ item.name }}
                          </v-btn>
                        </v-list-item>

                        <v-divider class="my-2" />

                        <v-list-subheader>Sonstiges</v-list-subheader>

                        <v-list-item
                          v-for="(item, index) in nogroup"
                          :key="'n-' + index"
                        >
                          <v-btn
                            variant="text"
                            block
                            class="justify-start"
                            @click.stop="moveToGroup(item, action)"
                          >
                            {{ item.name }}
                          </v-btn>
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <!-- Delete Action -->
                    <v-tooltip text="Löschen" location="bottom" color="surface" content-class="tooltip">
                      <template #activator="{ props }">
                        <span v-bind="props" class="delete-icon-wrapper" @click.stop>
                          <delete-missile-switch
                            activator-icon
                            activatorClass="action-icon-button action-delete-button"
                            @click="deleteAction(action)"
                          />
                        </span>
                      </template>
                    </v-tooltip>

                    <!-- Einzel-Berechnung toggle -->
                    <v-tooltip v-if="!action.group" text="Maßnahme berechnen" location="bottom" color="surface" content-class="tooltip">
                      <template #activator="{ props }">
                        <v-checkbox
                          v-bind="props"
                          :model-value="store.isActionChecked(action.id)"
                          @click.stop
                          @update:modelValue="(newVal) => store.setActionChecked(action.id, newVal)"
                          density="compact"
                          hide-details
                          class="control-inline action-calc-checkbox"
                          aria-label="Maßnahme berechnen"
                        />
                      </template>
                    </v-tooltip>
                  </div>
                  
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>

        <!-- Optional skeleton while loading method texts -->
        <div v-if="isLoadingMethods" class="pa-4 text-caption text-medium-emphasis">
          Lade Methoden …
        </div>
      </div>
    </v-card-text>

    <!-- Footer Actions -->
    <v-divider />

    <div class="card-footer-actions">
      <v-dialog v-model="dialogCreateGroup" persistent max-width="640">
        <template #activator="{ props }">
          <v-btn color="primary" variant="tonal" v-bind="props">
            <v-icon start>mdi-folder-plus</v-icon>
            Gruppe hinzufügen
          </v-btn>
        </template>

        <v-card>
          <v-card-title class="text-h6">Gruppe hinzufügen</v-card-title>
          <v-card-text>
            <v-text-field label="Name" v-model="newActionGroupName" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="red" variant="text" @click="cancelCreateGroup">
              Abbrechen
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :disabled="!newActionGroupName"
              @click="newActionGroup"
            >
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-btn color="primary" variant="elevated" @click="newAction">
        <v-icon start>mdi-plus</v-icon>
        Maßnahme hinzufügen
      </v-btn>
    </div>
  </v-card>

  <!-- Project Actions -->
  <v-card class="project-actions-card mt-4" elevation="1">
    <div class="project-actions-content">
      <div>
        <div class="text-subtitle-1 font-weight-bold">
          Projektaktionen
        </div>
        <div class="text-body-2 text-medium-emphasis">
          Projektdetails bearbeiten oder Projekt entfernen.
        </div>
      </div>

      <div class="project-actions-buttons">
        <delete-missile-switch activatorClass="ma-0" @click="deleteProject">
          Projekt löschen
        </delete-missile-switch>

        <v-btn color="primary" variant="tonal" @click="editProjectDetails">
          <v-icon start>mdi-pencil</v-icon>
          Projektdetails bearbeiten
        </v-btn>
      </div>
    </div>
  </v-card>
</template>





<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import api from '@/plugins/axios';
import { useProjectStore } from '@/stores/projectStore';
import DeleteMissileSwitch from '@/components/DeleteMissileSwitch.vue';
import HelpBar from '@/components/HelpBar.vue';
import ProjectDashboardHelp from '@/components/help/ProjectDashboardHelp.vue';
import ProjectPageNav from '@/components/ProjectPageNav.vue';

const route = useRoute();
const router = useRouter();
const store = useProjectStore();

// Reactive state
const methodTexts = ref(null);
const isLoadingMethods = ref(false);

const newActionGroupName = ref(null);
const dialogGroupId = ref(null);
const dialogCreateGroup = ref(false);
const dialogDeleteGroup = ref(false);
const dialogRenameGroup = ref(false);

const newActionName = ref(null);
const dialogActionData = ref(null);
const dialogRenameAction = ref(false);

// Computed
const projectId = computed(() => route.params.projectId);
const project = computed(() => store.currentProject);
const actionList = computed(() => store.currentProjectActions || []);
const groupsList = computed(() => store.currentProjectActionGroups || []);
const groups = computed(() =>
  (store.currentProjectActionGroups || []).reduce((acc, curr) => {
    if (curr && curr.id != null) acc[curr.id] = curr;
    return acc;
  }, {})
);
const byActionGroup = computed(() =>
  actionList.value.reduce((acc, curr) => {
    const key = curr.group ?? null;
    (acc[key] = acc[key] || []).push(curr);
    return acc;
  }, {})
);

// "ohne Zuordnung" item
const nogroup = ref([{ id: null, name: 'ohne Zuordnung', project: projectId.value }]);

// Sync title
watch(
  project,
  (proj) => {
    if (proj && proj.name) store.setTitle(proj.name);
  },
  { immediate: true }
);

// Lifecycle
// onBeforeMount(() => {
//   fetch();
//   fetchMethodTexts();
// });

onMounted(async() => {
  // Pre-select help for this view so fallback help doesn't render
  store.setHelpComponent(ProjectDashboardHelp);

  
  try {
      // 1) Refresh actions on backend
      await api.post(`/projects/${projectId.value}/refresh-actions/`);
    } catch (err) {
      console.error('Refresh-Endpoint fehlgeschlagen:', err);
      // Optional toast
      // toast.error('Konnte Maßnahmen nicht automatisch aktualisieren.');
    } finally {
      // 2) Fetch page data regardless
      await fetch();
      await fetchMethodTexts();
    }

});

// Helper
function isUngroupedKey(key) {
  return key === "null"; // guards null/undefined
}

// Data fetch
function fetch() {
  return store.fetchCurrentProject(projectId.value);
}

function fetchMethodTexts() {
  isLoadingMethods.value = true;
  api
    .get('/method_text/')
    .then((response) => {
      methodTexts.value = response.data;
    })
    .finally(() => {
      isLoadingMethods.value = false;
    });
}

// Toggles & checks
// function checkNoCalc(item) {
//   return item ? store.noCalc.includes(item.id) : false;
// }

// function checkNoCalcGroup(item) {
//   return item ? store.noCalcGroup.includes(item.id) : false;
// }

// function addNoCalc(item) {
//   if (store.noCalc.includes(item.id)) {
//     store.noCalc = store.noCalc.filter((i) => i !== item.id);
//   } else {
//     store.noCalc.push(item.id);
//   }
//   fetch();
// }

// function addNoCalcGroup(item) {
//   const actionsInGroup = actionList.value.filter((i) => i.group === item.id).map((i) => i.id);
//   const actionsInGroupSet = new Set(actionsInGroup);

//   if (store.noCalcGroup.includes(item.id)) {
//     store.noCalcGroup = store.noCalcGroup.filter((i) => i !== item.id);
//     store.noCalc = store.noCalc.filter((x) => !actionsInGroupSet.has(x));
//   } else {
//     store.noCalcGroup.push(item.id);
//     store.noCalc.push(...actionsInGroup);
//   }
//   fetch();
// }

function checkMethodTexts(action) {
  return methodTexts.value ? (methodTexts.value[action.method] ?? '') : '';
}

/*
 * Representative icon per method type so each measure in the list is
 * recognisable at a glance. Keyed by the method key stored on the action.
 */
const METHOD_ICONS = {
  boiler: 'mdi-water-boiler',
  chiller: 'mdi-snowflake',
  hvac: 'mdi-hvac',
  drp1: 'mdi-pump',
  wrp1: 'mdi-pump',
  pump1: 'mdi-pump',
  pump2: 'mdi-pump',
  fan1: 'mdi-fan',
  fan2: 'mdi-fan',
  lighting1: 'mdi-lightbulb-outline',
  lighting2: 'mdi-lightbulb-on-outline',
  electricdrive1: 'mdi-engine-outline',
  electricdrive2: 'mdi-engine-outline',
  'free-con': 'mdi-power-plug-outline',
  freegen: 'mdi-generator-stationary',
  freeren: 'mdi-solar-power-variant',
  pv: 'mdi-solar-panel-large',
};

function methodIcon(action) {
  return METHOD_ICONS[action?.method] || 'mdi-lightning-bolt-outline';
}

function getGroup(groupId) {
  return groups.value[groupId];
}

function getGroupName(groupId) {
  return (groups.value[groupId] && groups.value[groupId].name) ?? 'n/a';
}

function storeGroupIdForDialog(id) {
  dialogGroupId.value = typeof id === 'string' ? Number(id) : id;
}

function storeActionDataForDialog(data) {
  dialogActionData.value = data;
}

// Dialog helpers
function cancelCreateGroup() {
  dialogCreateGroup.value = false;
  newActionGroupName.value = null;
}

function cancelRenameGroup() {
  dialogRenameGroup.value = false;
  newActionGroupName.value = null;
}

function cancelRenameAction() {
  dialogRenameAction.value = false;
  newActionName.value = null;
}

// API actions
function newActionGroup() {
  api
    .post('/actiongroups/', {
      project: projectId.value,
      name: newActionGroupName.value,
    })
    .then(() => {
      toast.success('Gruppe erfolgreich erstellt.');
      dialogCreateGroup.value = false;
      newActionGroupName.value = null;
      fetch();
    })
    .catch(() => {
      toast.error('Fehler: Name muss eindeutig sein.');
    });
}

function renameActionGroup() {
  api
    .patch(`/actiongroups/${dialogGroupId.value}/`, {
      name: newActionGroupName.value,
    })
    .then(() => {
      toast.success('Gruppe erfolgreich umbenannt.');
      dialogRenameGroup.value = false;
      newActionGroupName.value = null;
      fetch();
    })
    .catch(() => {
      toast.error('Fehler beim Umbenennen der Gruppe.');
    });
}

function deleteActionGroup(group) {
  api
    .get(`/actiongroups/${dialogGroupId.value}/`)
    .then(() => {
      const actionListGroup = actionList.value.filter((i) => i.group === group.id);
      Promise.all(actionListGroup.map((action) => api.delete(`/actions/${action.id}/`))).then(() => {
        api
          .delete(`/actiongroups/${dialogGroupId.value}/`)
          .then(() => {
            toast.success('Gruppe erfolgreich gelöscht.');
            dialogDeleteGroup.value = false;
            fetch();
          })
          .catch(() => {
            toast.error('Fehler beim Löschen der Gruppe.');
            dialogDeleteGroup.value = false;
          });
      });
    })
    .catch(() => {
      toast.error('Fehler beim Löschen der Gruppe.');
      dialogDeleteGroup.value = false;
    });
}


function newAction() {
  router.push({ name: 'NewAction', params: { projectId: projectId.value } });
}

function editAction(action) {
  router.push({ name: 'Action', params: { projectId: projectId.value, actionId: action.id } });
}

function editProjectDetails() {
  router.push({ name: 'ProjectDetails', params: { projectId: projectId.value } });
}

function deleteProject() {
  api
    .delete(`/projects/${projectId.value}/`)
    .then(() => {
      toast.success(`Projekt <b>${project.value?.name ?? ''}</b> erfolgreich gelöscht.`);
      router.go(-1);
    })
    .catch(() => {
      toast.error('Fehler beim Löschen des Projekts.');
    });
}

function deleteAction(action) {
  api
    .delete(`/actions/${action.id}/`)
    .then(() => {
      toast.success(`Maßnahme <b>${action.name}</b> erfolgreich gelöscht.`);
      fetch();
    })
    .catch(() => {
      toast.error('Fehler beim Löschen der Maßnahme.');
    });
}

function cloneAction(action) {
  const clonedAction = { ...action, name: `${action.name} (Kopie)` };
  api
    .post('/actions/', clonedAction)
    .then(() => {
      toast.success(`Maßnahme <b>${clonedAction.name}</b> erfolgreich dupliziert.`);
      fetch();
    })
    .catch((error) => {
      console.error('API Error:', error?.response?.data ?? error);
      toast.error('Fehler beim Duplizieren der Maßnahme.');
    });
}

function cloneActionGroup(group) {
  const newGroup = { ...group, name: `${group.name} (Kopie)` };
  api
    .post('/actiongroups/', newGroup)
    .then(() => {
      api
        .get('/actiongroups/', { params: { project: projectId.value } })
        .then((response) => {
          const newGroupId = response.data.find((i) => i.name === newGroup.name)?.id;
          const actionListGroup = actionList.value.filter((i) => i.group === group.id);
          const clonedActions = actionListGroup.map((a) => ({
            ...a,
            group: newGroupId,
            name: `${a.name} (Kopie)`,
          }));
          Promise.all(clonedActions.map((action) => api.post('/actions/', action)));
          toast.success(`Gruppe <b>${group.name}</b> erfolgreich dupliziert.`);
        })
        .catch(() => {
          toast.error('Fehler beim Duplizieren der Gruppe.');
        });
      setTimeout(fetch, 500);
    })
    .catch(() => {
      toast.error('Gruppe bereits dupliziert. Bitte umbenennen.');
    });
}

function moveToGroup(item, action) {
  const updatedAction = { ...action, group: item.id };
  api
    .put(`/actions/${action.id}/`, updatedAction)
    .then(() => {
      toast.success('Maßnahme erfolgreich der Gruppe hinzugefügt.');
      fetch();
    })
    .catch(() => {
      toast.error('Fehler beim Zuordnen der Maßnahme.');
    });
}

function renameAction(name, action) {
  const updatedAction = { ...action, name };
  api
    .put(`/actions/${action.id}/`, updatedAction)
    .then(() => {
      toast.success('Maßnahme erfolgreich umbenannt.');
      dialogRenameAction.value = false;
      newActionName.value = null;
      fetch();
    })
    .catch(() => {
      toast.error('Fehler beim Umbenennen der Maßnahme.');
    });
}

</script>





<style scoped lang="scss">
.actions-card {
  overflow: hidden;
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.actions-toolbar {
  min-height: auto;
  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.025)
    );
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.actions-toolbar-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}

.actions-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 240px;
}

.actions-title-icon {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.actions-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.toolbar-button {
  text-transform: none;
  font-weight: 600;
}

.actions-card-body {
  padding: 1.25rem;
}

.dashboard-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem;
  margin-bottom: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.dashboard-summary-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.dashboard-summary-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.table-wrapper {
  max-height: 60vh;
  overflow: auto;
  border-radius: 22px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background-color: rgb(var(--v-theme-surface));
}

.actions-table {
  background-color: transparent;
}

.actions-table thead tr th.sticky-header {
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
  box-shadow: 0 1px 0 rgba(var(--v-theme-on-surface), 0.08);
}

.actions-table tbody tr {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.action-row {
  transition:
    background-color 160ms ease,
    box-shadow 160ms ease;
}

.action-row:hover {
  background-color: rgba(var(--v-theme-primary), 0.075);
}

.action-row:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.45);
  outline-offset: -2px;
}

.group-row {
  background-color: rgba(var(--v-theme-primary), 0.055);
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.group-ungrouped {
  background-color: rgba(var(--v-theme-secondary), 0.055);
}

.group-cell {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}

.group-row-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.group-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  padding-inline: 0.35rem;
  color: rgb(var(--v-theme-on-surface));
}


.group-title-icon {
  flex-shrink: 0;
}

.group-title-text {
  min-width: 0;
}

.group-title-main {
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.25;
  color: rgb(var(--v-theme-on-surface));
}

.group-title-meta {
  margin-top: 0.15rem;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.2;
  color: rgba(var(--v-theme-on-surface), 0.62);
}


.group-controls {
  display: flex;
  align-items: center; 
  justify-content: flex-end;
  gap: 0.25rem;

}

.action-name-cell {
  display: flex;
  align-items: center;
  min-width: 260px;
}

.action-name-text {
  min-width: 0;
}

.action-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}


.action-icon-button {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
}


.action-icon-button:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Keep the glyph in step with the project list's action row. */
.action-icon-button .v-icon {
  font-size: 18px;
}


.action-delete-button {
  color: rgb(var(--v-theme-error));
}

.action-delete-button:hover {
  background-color: rgba(var(--v-theme-error), 0.08);
}


.delete-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/*
 * The delete button lives inside <DeleteMissileSwitch>, so this component's
 * scoped styles cannot reach it - the class is applied, but the scope id
 * belongs to the child. Pierce the boundary so it matches the sizing of the
 * sibling .action-icon-button elements instead of rendering at Vuetify's
 * default icon-button size.
 */
.delete-icon-wrapper :deep(.action-icon-button) {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
}

/* The glyph lives in the child component too, so it needs :deep() as well -
 * without this the delete icon would stay large while its siblings shrink. */
.delete-icon-wrapper :deep(.action-icon-button .v-icon) {
  font-size: 18px;
}

.delete-icon-wrapper :deep(.action-delete-button) {
  color: rgb(var(--v-theme-error));
}

.delete-icon-wrapper :deep(.action-delete-button:hover) {
  background-color: rgba(var(--v-theme-error), 0.08);
}

.action-calc-checkbox {
  margin-left: 0.25rem;
}

.action-calc-checkbox :deep(.v-selection-control) {
  min-height: 36px;
}

.action-calc-checkbox :deep(.v-selection-control__wrapper) {
  margin-block: 0;
}

.method-type-chip {
  text-transform: none;
}

.col-name {
  width: 36%;
}

.col-method {
  width: 28%;
}

.col-method-type {
  width: 16%;
}

.col-options {
  width: 24%;
}

.pointer {
  cursor: pointer;
}

.dots-button {
  box-shadow: none;
  background: transparent;
  color: inherit;
}

.actions-menu,
.assign-menu {
  border-radius: 18px;
}

.assign-menu {
  min-width: 280px;
}

.group-calc-checkbox :deep(.v-selection-control__wrapper),
.control-inline :deep(.v-selection-control__wrapper) {
  margin-block: 0;
}

.card-footer-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  flex-wrap: wrap;
}

.card-footer-actions .v-btn {
  text-transform: none;
  font-weight: 600;
}

.project-actions-card {
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.project-actions-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
}

.project-actions-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.empty-state {
  display: grid;
  place-items: center;
  min-height: 300px;
}

.empty-state-sheet {
  width: min(100%, 620px);
  border-radius: 28px;
  background-color: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.08);
}

.empty-icon {
  width: 82px;
  height: 82px;
  margin: 0 auto 1rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.font-medium {
  font-weight: 550;
}

.text-truncate {
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td),
:deep(.v-table .v-table__wrapper > table > thead > tr > th) {
  padding-top: 12px;
  padding-bottom: 12px;
}

@media (max-width: 1100px) {
  .actions-toolbar-content {
    align-items: stretch;
    flex-direction: column;
  }

  .actions-toolbar-actions {
    justify-content: flex-start;
  }

  .dashboard-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .dashboard-summary-right {
    justify-content: flex-start;
  }

  .project-actions-content {
    align-items: stretch;
    flex-direction: column;
  }

  .project-actions-buttons {
    justify-content: flex-start;
  }
}

@media (max-width: 900px) {
  .table-wrapper {
    max-height: 52vh;
  }

  .col-method-type {
    display: none;
  }
}

@media (max-width: 700px) {
  .actions-card-body {
    padding: 1rem;
  }

  .actions-toolbar-actions,
  .card-footer-actions,
  .project-actions-buttons,
  .empty-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .actions-toolbar-actions .v-btn,
  .card-footer-actions .v-btn,
  .project-actions-buttons .v-btn,
  .empty-actions .v-btn {
    width: 100%;
  }

}
</style>
